import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Calendar, Clock, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';

const MemberMyClasses = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [pastClasses, setPastClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    reservation: null
  });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/app/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        apiClient.get('/app/my-reservations', { params: { token, status: 'upcoming' } }),
        apiClient.get('/app/my-reservations', { params: { token, status: 'past' } })
      ]);
      
      setUpcomingClasses(upcomingRes.data.reservations || []);
      setPastClasses(pastRes.data.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/app/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // 한국 서비스 기준 KST (Asia/Seoul)
    const parts = new Intl.DateTimeFormat('ko-KR', {
      timeZone: 'Asia/Seoul',
      month: 'numeric', day: 'numeric', weekday: 'short',
    }).formatToParts(date);
    const get = (t) => parts.find(p => p.type === t)?.value || '';
    return `${get('month')}월 ${get('day')}일 (${get('weekday')})`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    return date.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatInstructor = (instructor) => {
    if (!instructor) return null;
    if (/^instructor_\d+$/i.test(instructor)) return null;
    if (/^[a-z0-9_-]+$/i.test(instructor) && instructor.length < 3) return null;
    return instructor;
  };

  const handleCancelClick = (reservation) => {
    setCancelDialog({ open: true, reservation });
  };

  const handleConfirmCancel = async () => {
    const reservation = cancelDialog.reservation;
    if (!reservation) return;
    
    setCancelling(true);
    try {
      await apiClient.delete(`/app/reservations/${reservation.reservation_id}`, {
        params: { token }
      });
      toast.success('예약이 취소되었습니다');
      setCancelDialog({ open: false, reservation: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || '예약 취소에 실패했습니다');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'attended':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#22C55E]/15 text-[#22C55E]">
            <CheckCircle className="w-3 h-3 mr-1" />
            출석
          </span>
        );
      case 'no_show':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#EF4444]/15 text-[#EF4444]">
            <XCircle className="w-3 h-3 mr-1" />
            노쇼
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#52525B]/20 text-[#71717A]">
            <MinusCircle className="w-3 h-3 mr-1" />
            취소됨
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24">
      {/* Header */}
      <div className="bg-[#0F1115] sticky top-0 z-10">
        <div className="px-5 pt-12 pb-4">
          <h1 className="text-xl font-bold text-white">내 수업</h1>
        </div>
        
        {/* Tabs */}
        <div className="flex mx-4 bg-[#1A1D23] rounded-xl p-1 mb-4">
          <button
            onClick={() => setActiveTab('upcoming')}
            data-testid="tab-upcoming"
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeTab === 'upcoming'
                ? 'bg-[#FF7A00] text-white'
                : 'text-[#71717A] hover:text-[#A1A1AA]'
            }`}
          >
            예정된 수업
            {upcomingClasses.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full ${
                activeTab === 'upcoming' 
                  ? 'bg-white/20 text-white' 
                  : 'bg-[#22252E] text-[#71717A]'
              }`}>
                {upcomingClasses.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('past')}
            data-testid="tab-past"
            className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
              activeTab === 'past'
                ? 'bg-[#FF7A00] text-white'
                : 'text-[#71717A] hover:text-[#A1A1AA]'
            }`}
          >
            지난 수업
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4">
        {loading ? (
          <div className="text-center py-16 text-[#52525B]">로딩 중...</div>
        ) : activeTab === 'upcoming' ? (
          upcomingClasses.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-[#2A2E38]" />
              <p className="text-[#52525B] mb-4">예정된 수업이 없습니다</p>
              <button
                onClick={() => navigate('/app/book')}
                data-testid="go-book"
                className="bg-[#FF7A00] hover:bg-[#FF8C42] text-white font-medium px-6 py-2.5 rounded-xl transition-colors active:scale-[0.98]"
              >
                수업 예약하기
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingClasses.map((cls) => {
                const instructorName = formatInstructor(cls.instructor);
                
                return (
                  <div key={cls.reservation_id} data-testid={`upcoming-${cls.reservation_id}`} className="bg-[#1A1D23] rounded-xl p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#52525B] mb-1">{formatDate(cls.scheduled_at)}</div>
                        
                        <div className="flex items-center text-sm text-[#71717A] mb-1.5">
                          <Clock className="h-3.5 w-3.5 mr-1 text-[#52525B]" />
                          {formatTime(cls.scheduled_at)} ~ {formatTime(cls.end_time)}
                        </div>
                        
                        <h3 className="font-semibold text-white">{cls.class_name}</h3>
                        
                        {instructorName && (
                          <p className="text-xs text-[#52525B] mt-1">{instructorName}</p>
                        )}
                        
                        <div className="mt-2">
                          <span className="inline-flex items-center text-xs text-[#22C55E]">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {cls.status === 'waitlist' ? '대기 중' : '예약완료'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="ml-2 flex-shrink-0">
                        {cls.can_cancel ? (
                          <button
                            onClick={() => handleCancelClick(cls)}
                            data-testid={`cancel-${cls.reservation_id}`}
                            className="text-xs text-[#EF4444] border border-[#EF4444]/20 px-3 py-1.5 rounded-lg hover:bg-[#EF4444]/10 transition-colors"
                          >
                            취소하기
                          </button>
                        ) : (
                          <span className="text-[10px] text-[#52525B]">
                            {cls.cancel_message || '취소불가'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          pastClasses.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-[#2A2E38]" />
              <p className="text-[#52525B]">지난 수업 기록이 없습니다</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pastClasses.map((cls, idx) => {
                const instructorName = formatInstructor(cls.instructor);
                const uniqueKey = cls.attendance_id || cls.reservation_id || idx;
                
                return (
                  <div key={uniqueKey} data-testid={`past-${uniqueKey}`} className="bg-[#1A1D23]/60 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-xs text-[#52525B] mb-1">{formatDate(cls.scheduled_at)}</div>
                        
                        <div className="flex items-center text-xs text-[#52525B] mb-1">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTime(cls.scheduled_at)} ~ {formatTime(cls.end_time)}
                        </div>
                        
                        <h3 className="font-medium text-[#A1A1AA]">{cls.class_name}</h3>
                        
                        {instructorName && (
                          <p className="text-xs text-[#52525B] mt-1">{instructorName}</p>
                        )}
                      </div>
                      
                      <div className="ml-4">
                        {getStatusBadge(cls.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => !cancelling && setCancelDialog({ open, reservation: cancelDialog.reservation })}>
        <DialogContent className="max-w-sm bg-[#1A1D23] border-[#2A2E38] text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-center">예약 취소</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {cancelDialog.reservation && (
              <div className="text-center">
                <p className="text-[#A1A1AA] mb-2">다음 수업 예약을 취소하시겠습니까?</p>
                <p className="font-bold text-white">{cancelDialog.reservation.class_name}</p>
                <p className="text-sm text-[#71717A]">
                  {formatDate(cancelDialog.reservation.scheduled_at)} {formatTime(cancelDialog.reservation.scheduled_at)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCancelDialog({ open: false, reservation: null })}
              disabled={cancelling}
              className="flex-1 bg-transparent border-[#3A3F4A] text-[#A1A1AA] hover:bg-[#22252E] hover:text-white"
            >
              아니오
            </Button>
            <Button 
              onClick={handleConfirmCancel}
              disabled={cancelling}
              data-testid="confirm-cancel"
              className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] border-0 text-white"
            >
              {cancelling ? '취소 중...' : '예약 취소'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav activeTab="my-classes" />
    </div>
  );
};

export default MemberMyClasses;
